var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { baseApi } from "../api/baseApi";
export const projectsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // The caller's own companies only. Identity is resolved server-side from the token;
        // the arg is retained purely as the cache key / skip gate (see CompanyContext).
        getCompanies: builder.query({
            query: () => ({
                url: "Project/entities/by-email",
            }),
            providesTags: ["Companies"],
        }),
        // All companies, unscoped. Admin-only on the backend (Admin/SuperAdmin).
        getAllCompanies: builder.query({
            query: () => ({
                url: "Project/entities",
            }),
            providesTags: ["Companies"],
        }),
        getProjects: builder.query({
            query: () => "Project",
            providesTags: ["Projects"],
        }),
        getProjectById: builder.query({
            query: (id) => `Project/${id}`,
            providesTags: (_result, _error, id) => [{ type: "Projects", id }],
        }),
        getProjectCostValues: builder.query({
            query: ({ projectId, projectNumber, childProjectNumber, companyId, isChildProject, }) => ({
                url: "ProjectCostValues",
                params: {
                    projectId,
                    projectNumber,
                    childProjectNumber,
                    companyId,
                    isChildProject: isChildProject !== null && isChildProject !== void 0 ? isChildProject : false,
                },
            }),
            providesTags: ["ProjectCostValues"],
        }),
        createProjectCostValue: builder.mutation({
            query: (data) => ({
                url: "ProjectCostValues",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["ProjectCostValues", "Projects"],
        }),
        // The overview grid's data source. A query (not a mutation) so the result is
        // cached per request: returning to the overview or clearing a filter paints the
        // rows it already has while a refetch runs in the background, instead of staring
        // at a skeleton for the full round trip every time.
        getProjectSnapshots: builder.query({
            query: (request) => ({
                url: "Project/snapshots",
                method: "POST",
                body: request,
            }),
            providesTags: ["Projects"],
            // Keep a request's rows around long enough to cover a detour into a project
            // and back (the server-side cache for the same request is 3 minutes).
            keepUnusedDataFor: 300,
        }),
        getProjectTree: builder.query({
            query: ({ topProjectNumber, childProject, entityName, aanneemsom, companyId, isChildProject, }) => {
                const params = {
                    topProjectNumber,
                    entityName,
                };
                if (childProject) {
                    params.childProject = childProject;
                }
                if (aanneemsom) {
                    params.aanneemsom = aanneemsom;
                }
                if (companyId) {
                    params.companyId = companyId;
                }
                if (isChildProject !== undefined) {
                    params.isChildProject = String(isChildProject);
                }
                return {
                    url: "Project/tree",
                    params: params,
                };
            },
            providesTags: ["Projects"],
        }),
        getCombinedMetrics: builder.query({
            query: ({ parentProjectNumber, childProjectNumber, companyId, isChildProject, nodeDepth, }) => ({
                url: "Project/combined-metrics",
                params: {
                    parentProjectNumber,
                    childProjectNumber,
                    companyId,
                    isChildProject: isChildProject !== null && isChildProject !== void 0 ? isChildProject : false,
                    nodeDepth,
                },
            }),
            providesTags: ["Projects"],
        }),
        getForecastAdjustment: builder.query({
            query: ({ parentProjectNumber, childProjectNumber, companyId }) => ({
                url: "ForecastAdjustments/by-project",
                params: {
                    projectNumber: parentProjectNumber,
                    childProjectNumber,
                    companyId,
                },
            }),
            transformResponse: (response) => {
                return response;
            },
        }),
        createForecastAdjustment: builder.mutation({
            query: (body) => ({
                url: "ForecastAdjustments",
                method: "POST",
                body: {
                    ProjectNumber: body.projectNumber,
                    ProjectId: body.projectId,
                    ChildProjectNumber: body.childProjectNumber,
                    PocOverrule: body.pocOverrule,
                    AdjustmentCategory: body.adjustmentCategory,
                    Comment: body.comment,
                    CompanyId: body.companyId,
                },
            }),
            invalidatesTags: ["Projects"],
        }),
        updateForecastAdjustment: builder.mutation({
            query: (_a) => {
                var { id } = _a, body = __rest(_a, ["id"]);
                return ({
                    url: `ForecastAdjustments/${id}`,
                    method: "PUT",
                    body: {
                        ChildProjectNumber: body.childProjectNumber,
                        PocOverrule: body.pocOverrule,
                        AdjustmentCategory: body.adjustmentCategory,
                        Comment: body.comment,
                        CompanyId: body.companyId,
                        IsActive: body.isActive,
                        IsDeleted: body.isDeleted,
                    },
                });
            },
            invalidatesTags: ["Projects"],
        }),
        getUsersSummary: builder.query({
            query: () => "CompanyUsers/summary",
            providesTags: ["Companies"],
        }),
        createUser: builder.mutation({
            query: (body) => {
                var _a;
                return ({
                    url: "CompanyUsers",
                    method: "POST",
                    body: Object.assign({ companyIds: body.companyIds, userEmail: body.userEmail, username: body.username, allCompanies: (_a = body.allCompanies) !== null && _a !== void 0 ? _a : false }, (body.roleId && { roleId: body.roleId })),
                });
            },
            invalidatesTags: ["Companies"],
        }),
        updateUserStatus: builder.mutation({
            query: ({ userId, body }) => ({
                url: `CompanyUsers/${userId}/status`,
                method: "PATCH",
                body: {
                    status: body.status,
                },
            }),
            invalidatesTags: ["Companies"],
        }),
        deleteUser: builder.mutation({
            query: ({ userId }) => ({
                url: `CompanyUsers/${userId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Companies"],
        }),
        updateUser: builder.mutation({
            query: ({ userId, body }) => ({
                url: `CompanyUsers/${userId}`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: ["Companies"],
        }),
        // Roles APIs
        getAllRoles: builder.query({
            query: () => "Roles",
            providesTags: ["Roles"],
        }),
        createRole: builder.mutation({
            query: (body) => ({
                url: "Roles",
                method: "POST",
                body,
            }),
            invalidatesTags: ["Roles"],
        }),
        updateRole: builder.mutation({
            query: ({ roleId, body }) => ({
                url: `Roles/${roleId}`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["Roles"],
        }),
        deleteRole: builder.mutation({
            query: ({ roleId }) => ({
                url: `Roles/${roleId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Roles"],
        }),
        getUserPermissions: builder.query({
            query: (email) => ({
                url: "Roles/user-permissions",
                params: { email },
            }),
        }),
        getRolePermissions: builder.query({
            query: (roleId) => ({
                url: `Roles/${roleId}/permissions`,
            }),
            providesTags: (_result, _error, roleId) => [
                { type: "Roles", id: roleId },
            ],
        }),
        saveProjectMetric: builder.mutation({
            query: (body) => ({
                url: "ProjectMetricSaves",
                method: "POST",
                body,
            }),
            invalidatesTags: ["Projects"],
        }),
        getProjectValuationLevel: builder.query({
            query: ({ projectNumber, companyId, configMonth }) => `ProjectValuationLevel?projectNumber=${encodeURIComponent(projectNumber)}&companyId=${companyId}&configMonth=${encodeURIComponent(configMonth)}`,
            providesTags: (_result, _err, { projectNumber, companyId, configMonth }) => [
                {
                    type: "ProjectValuationLevel",
                    id: `${projectNumber}-${companyId}-${configMonth}`,
                },
            ],
        }),
        upsertProjectValuationLevel: builder.mutation({
            query: (body) => ({
                url: "ProjectValuationLevel",
                method: "PUT",
                body,
            }),
            invalidatesTags: (_result, _err, { projectNumber, companyId, configMonth }) => [
                {
                    type: "ProjectValuationLevel",
                    id: `${projectNumber}-${companyId}-${configMonth}`,
                },
            ],
        }),
        deleteProjectMetricSaves: builder.mutation({
            query: ({ companyId, parentProjectNumber, childProjectNumber, configMonth, }) => ({
                url: "ProjectMetricSaves",
                method: "DELETE",
                params: {
                    companyId,
                    parentProjectNumber,
                    childProjectNumber,
                    configMonth,
                },
            }),
        }),
        deleteProjectCostValues: builder.mutation({
            query: ({ companyId, projectNumber, childProjectNumber, configMonth, }) => ({
                url: "ProjectCostValues/by-project",
                method: "DELETE",
                params: { companyId, projectNumber, childProjectNumber, configMonth },
            }),
            invalidatesTags: ["ProjectCostValues"],
        }),
        deleteForecastAdjustmentByProject: builder.mutation({
            query: ({ projectNumber, childProjectNumber, companyId, configMonth, }) => ({
                url: "ForecastAdjustments/by-project",
                method: "DELETE",
                params: { projectNumber, childProjectNumber, companyId, configMonth },
            }),
        }),
        setProjectExclusion: builder.mutation({
            query: (request) => ({
                url: "ProjectExclusions/set",
                method: "POST",
                body: request,
            }),
        }),
    }),
});
export const { useGetCompaniesQuery, useGetAllCompaniesQuery, useGetProjectsQuery, useGetProjectByIdQuery, useGetProjectSnapshotsQuery, useGetProjectTreeQuery, useGetCombinedMetricsQuery, useGetForecastAdjustmentQuery, useCreateForecastAdjustmentMutation, useUpdateForecastAdjustmentMutation, useGetUsersSummaryQuery, useGetProjectCostValuesQuery, useCreateProjectCostValueMutation, useCreateUserMutation, useUpdateUserStatusMutation, useDeleteUserMutation, useUpdateUserMutation, useGetAllRolesQuery, useCreateRoleMutation, useUpdateRoleMutation, useDeleteRoleMutation, useGetUserPermissionsQuery, useGetRolePermissionsQuery, useSaveProjectMetricMutation, useGetProjectValuationLevelQuery, useUpsertProjectValuationLevelMutation, useDeleteProjectMetricSavesMutation, useDeleteForecastAdjustmentByProjectMutation, useDeleteProjectCostValuesMutation, useSetProjectExclusionMutation, } = projectsApi;
